import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameContext } from '../context/GameContext';
import { Container, PageTitle, GameGrid, GameCard } from '../styles/StyledComponents';
import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const WelcomeContainer = styled.div`
  text-align: center;
  margin: 2rem 0;
`;

const WelcomeCharacter = styled.div`
  font-size: 4rem;
  margin: 1rem;
  animation: ${bounce} 2s infinite;
  display: inline-block;
`;

const AnimatedText = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;

  span {
    font-size: 1.5rem;
    font-weight: bold;
    color: var(--primary-color);
    margin: 0 0.2rem;
    opacity: 0;
    animation: fadeIn 0.5s forwards;

    @keyframes fadeIn {
      to {
        opacity: 1;
      }
    }
  }
`;

const bubbleColors = [
  '#FF5722', '#2196F3', '#4CAF50', '#FFC107',
  '#9C27B0', '#3F51B5', '#E91E63', '#009688'
];

const Bubble = styled.div<{ size: number, x: number, y: number, color: string, delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background-color: ${props => props.color};
  top: ${props => props.y}%;
  left: ${props => props.x}%;
  opacity: 0;
  z-index: -1;
  animation: float 4s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;

  @keyframes float {
    0% {
      transform: translateY(0) scale(0);
      opacity: 0;
    }
    50% {
      opacity: 0.7;
    }
    100% {
      transform: translateY(-100px) scale(1);
      opacity: 0;
    }
  }
`;

const BubbleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 200px;
  bottom: 0;
  left: 0;
  pointer-events: none;
`;

const Instructions = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Home: React.FC = () => {
  const { games } = useGameContext();
  const navigate = useNavigate();
  const [welcomeText, setWelcomeText] = useState<string[]>([]);
  const [bubbles, setBubbles] = useState<Array<{ id: number, size: number, x: number, y: number, color: string, delay: number }>>([]);

  useEffect(() => {
    const text = "Let's Learn and Play!";
    const chars = text.split('');

    // Animate the welcome text one character at a time
    chars.forEach((char, index) => {
      setTimeout(() => {
        setWelcomeText(prev => [...prev, char]);
      }, index * 100);
    });

    // Create random bubbles
    const numBubbles = 20;
    const newBubbles = [];

    for (let i = 0; i < numBubbles; i++) {
      newBubbles.push({
        id: i,
        size: Math.random() * 30 + 10,
        x: Math.random() * 100,
        y: Math.random() * 20 + 80,
        color: bubbleColors[Math.floor(Math.random() * bubbleColors.length)],
        delay: Math.random() * 3
      });
    }

    setBubbles(newBubbles);
  }, []);

  const handleGameClick = (path: string) => {
    navigate(path);
  };

  return (
    <Container>
      <WelcomeContainer>
        <WelcomeCharacter>🎮</WelcomeCharacter>
        <AnimatedText>
          {welcomeText.map((char, index) => (
            <span key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </AnimatedText>
      </WelcomeContainer>

      <PageTitle>Welcome to Kid Games!</PageTitle>

      <Instructions>
        Pick a game below to start learning and having fun!<br />
        Each game will help you learn new skills in math and English.
      </Instructions>

      <GameGrid>
        {games.map(game => (
          <GameCard
            key={game.id}
            bgColor={game.color}
            onClick={() => handleGameClick(game.path)}
          >
            <div className="game-icon">
              <img
                src={`/assets/images/${game.icon}`}
                alt={game.name}
                // Fallback to an emoji if image doesn't load
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    // Add fallback emoji based on game type
                    let emoji = '';
                    switch (game.id) {
                      case 'number-quest':
                        emoji = '🔢';
                        break;
                      case 'pattern-puzzle':
                        emoji = '🧩';
                        break;
                      case 'sentence-scramble':
                        emoji = '📝';
                        break;
                      case 'phonics-factory':
                        emoji = '🔤';
                        break;
                      default:
                        emoji = '🎮';
                    }

                    const emojiDiv = document.createElement('div');
                    emojiDiv.style.fontSize = '4rem';
                    emojiDiv.textContent = emoji;
                    parent.appendChild(emojiDiv);
                  }
                }}
              />
            </div>
            <div className="game-info">
              <h3>{game.name}</h3>
              <p>{game.description}</p>
              <span className="game-category">{game.category}</span>
            </div>
          </GameCard>
        ))}
      </GameGrid>

      <BubbleContainer>
        {bubbles.map(bubble => (
          <Bubble
            key={bubble.id}
            size={bubble.size}
            x={bubble.x}
            y={bubble.y}
            color={bubble.color}
            delay={bubble.delay}
          />
        ))}
      </BubbleContainer>
    </Container>
  );
};

export default Home;
